import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { 
  ArrowRight, 
  ArrowLeft, 
  Upload,
  MapPin,
  Check,
  X,
  CreditCard,
  Home,
  Bath,
  Utensils,
  Building,
  TreePine,
  Grid3X3,
  Layers,
  Ruler
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FloorTilingPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  
  // Scroll to top when step changes - very aggressive implementation
  useEffect(() => {
    const scrollToTop = () => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
    
    // Immediate scroll
    scrollToTop();
    
    // Multiple attempts to ensure scroll sticks
    const timers = [
      setTimeout(scrollToTop, 0),
      setTimeout(scrollToTop, 10),
      setTimeout(scrollToTop, 50),
      setTimeout(scrollToTop, 100),
      setTimeout(scrollToTop, 200)
    ];
    
    // Also use requestAnimationFrame
    const raf = requestAnimationFrame(() => {
      scrollToTop();
      requestAnimationFrame(scrollToTop);
    });
    
    return () => {
      timers.forEach(t => clearTimeout(t));
      cancelAnimationFrame(raf);
    };
  }, [currentStep]);
  
  const [formData, setFormData] = useState({
    // Step 1: Room Type
    room_type: "",
    // Step 2: Floor Area
    floor_sqm: 15,
    area_size: "",
    // Step 3: Tile Type
    tile_type: "",
    // Step 4: Tile Size
    tile_size: "",
    // Step 5: Subfloor Prep
    subfloor_prep: "",
    // Step 6: Pattern
    pattern_layout: "",
    // Step 7: Additional Options
    additional_options: [],
    // Step 8: Photos
    photos: [],
    // Step 9: Postcode
    postcode: "",
    // Contact
    name: "",
    phone: "",
    email: "",
    booking_date: null,
    booking_time: ""
  });
  const [estimate, setEstimate] = useState(null);
  const [postcodeValid, setPostcodeValid] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);

  const totalSteps = 12;
  const progress = (currentStep / totalSteps) * 100;

  useEffect(() => {
    if (currentStep === 11) {
      fetchAvailableSlots();
    }
  }, [currentStep]);

  const fetchAvailableSlots = async () => {
    try {
      const response = await axios.get(`${API}/bookings/slots`);
      setAvailableSlots(response.data);
    } catch (error) {
      console.error("Error fetching slots:", error);
    }
  };

  const calculateEstimate = () => {
    let minTotal = 0;
    let maxTotal = 0;

    // Base tile installation costs per m²
    const tileCosts = {
      ceramic: { min: 40, max: 60 },
      porcelain: { min: 50, max: 70 },
      natural_stone: { min: 60, max: 90 },
      large_format: { min: 70, max: 120 },
      patterned: { min: 55, max: 85 }
    };
    
    const tileCost = tileCosts[formData.tile_type] || { min: 50, max: 70 };
    let baseMin = formData.floor_sqm * tileCost.min;
    let baseMax = formData.floor_sqm * tileCost.max;

    // Subfloor preparation costs
    const subfloorCosts = {
      no_prep: { min: 0, max: 0 },
      levelling: { perSqm: { min: 10, max: 20 } },
      backer_boards: { perSqm: { min: 20, max: 40 } },
      tile_removal: { perSqm: { min: 15, max: 30 } }
    };

    if (formData.subfloor_prep && formData.subfloor_prep !== "no_prep") {
      const prepCost = subfloorCosts[formData.subfloor_prep];
      if (prepCost?.perSqm) {
        baseMin += formData.floor_sqm * prepCost.perSqm.min;
        baseMax += formData.floor_sqm * prepCost.perSqm.max;
      }
    }

    // Pattern complexity multipliers
    const patternMultipliers = {
      straight: 1.0,
      brick: 1.05,
      herringbone: 1.2,
      diagonal: 1.1
    };
    const patternMult = patternMultipliers[formData.pattern_layout] || 1.0;
    baseMin *= patternMult;
    baseMax *= patternMult;

    minTotal = baseMin;
    maxTotal = baseMax;

    // Additional options
    const additionalCosts = {
      underfloor_heating: { min: 300, max: 900 },
      waterproof_membrane: { min: formData.floor_sqm * 8, max: formData.floor_sqm * 15 },
      tile_trim: { min: 50, max: 150 },
      grout_sealing: { min: formData.floor_sqm * 5, max: formData.floor_sqm * 10 }
    };

    formData.additional_options.forEach(option => {
      const cost = additionalCosts[option];
      if (cost) {
        minTotal += cost.min;
        maxTotal += cost.max;
      }
    });

    setEstimate({
      min_estimate: Math.round(minTotal / 10) * 10,
      max_estimate: Math.round(maxTotal / 10) * 10
    });
  };

  const validatePostcode = async () => {
    try {
      const response = await axios.get(`${API}/validate-postcode/${formData.postcode}`);
      setPostcodeValid(response.data.in_service_area);
      return response.data.in_service_area;
    } catch (error) {
      console.error("Error validating postcode:", error);
      return false;
    }
  };

  const handleNext = async () => {
    if (currentStep === 1 && !formData.room_type) {
      toast.error("Please select a room type");
      return;
    }
    if (currentStep === 3 && !formData.tile_type) {
      toast.error("Please select a tile type");
      return;
    }
    if (currentStep === 4 && !formData.tile_size) {
      toast.error("Please select a tile size");
      return;
    }
    if (currentStep === 5 && !formData.subfloor_prep) {
      toast.error("Please select subfloor preparation");
      return;
    }
    if (currentStep === 6 && !formData.pattern_layout) {
      toast.error("Please select a pattern layout");
      return;
    }
    if (currentStep === 9 && !formData.postcode) {
      toast.error("Please enter your postcode");
      return;
    }
    if (currentStep === 9) {
      await validatePostcode();
      calculateEstimate();
    }
    if (currentStep === 11 && (!formData.booking_date || !formData.booking_time)) {
      toast.error("Please select a date and time");
      return;
    }
    if (currentStep === 11) {
      await submitQuote();
      return;
    }

    // Scroll to top before changing step
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handleBack = () => {
    // Scroll to top before changing step
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const submitQuote = async () => {
    setLoading(true);
    try {
      const quoteResponse = await axios.post(`${API}/quotes`, {
        project_type: "floor",
        room_size: formData.area_size || "medium",
        tile_type: formData.tile_type,
        project_stage: "ready_now",
        photos: formData.photos,
        postcode: formData.postcode,
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        estimated_min: estimate?.min_estimate || 0,
        estimated_max: estimate?.max_estimate || 0,
        notes: `Room: ${formData.room_type}, Area: ${formData.floor_sqm}m², Pattern: ${formData.pattern_layout}`
      });
      
      const newQuoteId = quoteResponse.data.id;

      if (formData.booking_date && formData.booking_time) {
        await axios.post(`${API}/bookings`, {
          quote_id: newQuoteId,
          date: format(formData.booking_date, "yyyy-MM-dd"),
          time: formData.booking_time
        });
      }

      setCurrentStep(12);
      toast.success("Your floor tiling quote has been submitted!");
    } catch (error) {
      console.error("Error submitting quote:", error);
      toast.error("Failed to submit quote. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length + formData.photos.length > 5) {
      toast.error("Maximum 5 photos allowed");
      return;
    }

    for (const file of files) {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);

      try {
        const response = await axios.post(`${API}/upload`, formDataUpload);
        setFormData(prev => ({
          ...prev,
          photos: [...prev.photos, response.data.data]
        }));
      } catch (error) {
        toast.error("Failed to upload photo");
      }
    }
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const toggleOption = (value) => {
    setFormData(prev => ({
      ...prev,
      additional_options: prev.additional_options.includes(value)
        ? prev.additional_options.filter(v => v !== value)
        : [...prev.additional_options, value]
    }));
  };

  // Data definitions
  const roomTypes = [
    { value: "kitchen", label: "Kitchen Floor", icon: Utensils },
    { value: "bathroom", label: "Bathroom Floor", icon: Bath },
    { value: "hallway", label: "Hallway", icon: Home },
    { value: "living_room", label: "Living Room", icon: Home },
    { value: "commercial", label: "Commercial Floor", icon: Building },
    { value: "outdoor", label: "Outdoor Tiling", icon: TreePine }
  ];

  const areaSizes = [
    { value: "small", label: "Small Area", description: "Under 10 m²", defaultSqm: 8 },
    { value: "medium", label: "Medium Area", description: "10-25 m²", defaultSqm: 18 },
    { value: "large", label: "Large Area", description: "25-50 m²", defaultSqm: 35 },
    { value: "xlarge", label: "Large Open Floor", description: "50 m²+", defaultSqm: 60 }
  ];

  const tileTypes = [
    { value: "ceramic", label: "Ceramic Tile", price: "£40-£60/m²" },
    { value: "porcelain", label: "Porcelain Tile", price: "£50-£70/m²" },
    { value: "natural_stone", label: "Natural Stone Tile", price: "£60-£90/m²" },
    { value: "large_format", label: "Large Format Tile", price: "£70-£120/m²" },
    { value: "patterned", label: "Patterned Tiles", price: "£55-£85/m²" }
  ];

  const tileSizes = [
    { value: "small", label: "Small Tiles", description: "Under 300mm" },
    { value: "standard", label: "Standard Tiles", description: "300-600mm" },
    { value: "large", label: "Large Format Tiles", description: "600-900mm" },
    { value: "xlarge", label: "Extra Large Tiles", description: "900mm+" }
  ];

  const subfloorOptions = [
    { value: "no_prep", label: "No Preparation Needed", price: "£0" },
    { value: "levelling", label: "Self-levelling Compound Required", price: "+£10-£20/m²" },
    { value: "backer_boards", label: "New Tile Backer Boards Required", price: "+£20-£40/m²" },
    { value: "tile_removal", label: "Old Tiles Need Removal", price: "+£15-£30/m²" }
  ];

  const patternOptions = [
    { value: "straight", label: "Straight Lay", description: "Standard grid pattern", multiplier: "1x" },
    { value: "brick", label: "Brick Pattern", description: "Offset layout", multiplier: "+5%" },
    { value: "herringbone", label: "Herringbone", description: "Classic diagonal pattern", multiplier: "+20%" },
    { value: "diagonal", label: "Diagonal Layout", description: "45° angle pattern", multiplier: "+10%" }
  ];

  const additionalOptions = [
    { value: "underfloor_heating", label: "Underfloor Heating", price: "£300-£900" },
    { value: "waterproof_membrane", label: "Waterproof Membrane", price: "£8-£15/m²" },
    { value: "tile_trim", label: "Tile Edging Trim Installation", price: "£50-£150" },
    { value: "grout_sealing", label: "Grout Sealing", price: "£5-£10/m²" }
  ];

  const getAvailableTimes = () => {
    if (!formData.booking_date) return [];
    const dateStr = format(formData.booking_date, "yyyy-MM-dd");
    return availableSlots.filter(slot => slot.date === dateStr);
  };

  const handleAreaSizeSelect = (size) => {
    const sizeData = areaSizes.find(s => s.value === size);
    setFormData(prev => ({
      ...prev,
      area_size: size,
      floor_sqm: sizeData?.defaultSqm || 15
    }));
  };

  return (
    <div className="min-h-screen bg-white" data-testid="floor-tiling-page">
      {/* Progress Header */}
      <div className="sticky top-0 bg-white/95 backdrop-blur-md border-b border-gray-100 z-40">
        <div className="container-custom py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">
              Floor Tiling Estimator - Step {currentStep} of {totalSteps}
            </span>
            <span className="text-sm font-medium text-primary">
              {Math.round(progress)}% Complete
            </span>
          </div>
          <Progress value={progress} className="h-2" data-testid="floor-progress" />
        </div>
      </div>

      {/* Main Content */}
      <div className="container-custom py-12">
        <div className="max-w-3xl mx-auto">
          {/* Step 1: Room Type */}
          {currentStep === 1 && (
            <div className="animate-fade-in" data-testid="floor-step-1">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center font-montserrat">
                What room needs floor tiling?
              </h1>
              <p className="text-gray-600 text-center mb-8">
                Select the type of room for your floor tiling project
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {roomTypes.map((room) => (
                  <button
                    key={room.value}
                    onClick={() => setFormData({ ...formData, room_type: room.value })}
                    className={`funnel-card ${formData.room_type === room.value ? "selected" : ""}`}
                    data-testid={`room-${room.value}`}
                  >
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                      <room.icon className="w-5 h-5 text-gray-600" />
                    </div>
                    <span className="font-medium text-gray-900">{room.label}</span>
                    {formData.room_type === room.value && (
                      <Check className="w-5 h-5 text-primary ml-auto" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Floor Area */}
          {currentStep === 2 && (
            <div className="animate-fade-in" data-testid="floor-step-2">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center font-montserrat">
                Floor Area
              </h1>
              <p className="text-gray-600 text-center mb-8">
                Select the approximate size or enter exact measurements
              </p>
              
              <div className="grid sm:grid-cols-2 gap-3 mb-8">
                {areaSizes.map((size) => (
                  <button
                    key={size.value}
                    onClick={() => handleAreaSizeSelect(size.value)}
                    className={`funnel-card ${formData.area_size === size.value ? "selected" : ""}`}
                    data-testid={`area-${size.value}`}
                  >
                    <div className="flex-1 text-left">
                      <span className="font-medium text-gray-900 block">{size.label}</span>
                      <span className="text-sm text-gray-500">{size.description}</span>
                    </div>
                    {formData.area_size === size.value && (
                      <Check className="w-5 h-5 text-primary" />
                    )}
                  </button>
                ))}
              </div>

              <div className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <Label className="text-base font-medium">Exact Floor Area (m²)</Label>
                  <span className="text-2xl font-bold text-primary">{formData.floor_sqm} m²</span>
                </div>
                <Slider
                  value={[formData.floor_sqm]}
                  onValueChange={(value) => setFormData({ ...formData, floor_sqm: value[0] })}
                  min={2}
                  max={100}
                  step={1}
                  className="mb-2"
                  data-testid="floor-sqm-slider"
                />
                <div className="flex justify-between text-sm text-gray-500">
                  <span>2 m²</span>
                  <span>100 m²</span>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Tile Type */}
          {currentStep === 3 && (
            <div className="animate-fade-in" data-testid="floor-step-3">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center font-montserrat">
                Tile Type
              </h1>
              <p className="text-gray-600 text-center mb-8">
                Select the type of tile for your floor
              </p>
              <div className="space-y-3">
                {tileTypes.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setFormData({ ...formData, tile_type: type.value })}
                    className={`funnel-card w-full ${formData.tile_type === type.value ? "selected" : ""}`}
                    data-testid={`tile-${type.value}`}
                  >
                    <Grid3X3 className="w-5 h-5 text-gray-400" />
                    <div className="flex-1 text-left">
                      <span className="font-medium text-gray-900 block">{type.label}</span>
                      <span className="text-sm text-gray-500">{type.price}</span>
                    </div>
                    {formData.tile_type === type.value && (
                      <Check className="w-5 h-5 text-primary" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Tile Size */}
          {currentStep === 4 && (
            <div className="animate-fade-in" data-testid="floor-step-4">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center font-montserrat">
                Tile Size
              </h1>
              <p className="text-gray-600 text-center mb-8">
                Select the size of tiles you're using
              </p>
              <div className="space-y-3">
                {tileSizes.map((size) => (
                  <button
                    key={size.value}
                    onClick={() => setFormData({ ...formData, tile_size: size.value })}
                    className={`funnel-card w-full ${formData.tile_size === size.value ? "selected" : ""}`}
                    data-testid={`size-${size.value}`}
                  >
                    <Ruler className="w-5 h-5 text-gray-400" />
                    <div className="flex-1 text-left">
                      <span className="font-medium text-gray-900 block">{size.label}</span>
                      <span className="text-sm text-gray-500">{size.description}</span>
                    </div>
                    {formData.tile_size === size.value && (
                      <Check className="w-5 h-5 text-primary" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 5: Subfloor Preparation */}
          {currentStep === 5 && (
            <div className="animate-fade-in" data-testid="floor-step-5">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center font-montserrat">
                Subfloor Preparation
              </h1>
              <p className="text-gray-600 text-center mb-8">
                Does the subfloor need any preparation?
              </p>
              <div className="space-y-3">
                {subfloorOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setFormData({ ...formData, subfloor_prep: option.value })}
                    className={`funnel-card w-full ${formData.subfloor_prep === option.value ? "selected" : ""}`}
                    data-testid={`subfloor-${option.value}`}
                  >
                    <Layers className="w-5 h-5 text-gray-400" />
                    <div className="flex-1 text-left">
                      <span className="font-medium text-gray-900 block">{option.label}</span>
                    </div>
                    <span className="text-sm font-medium text-primary">{option.price}</span>
                    {formData.subfloor_prep === option.value && (
                      <Check className="w-5 h-5 text-primary ml-2" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 6: Pattern Layout */}
          {currentStep === 6 && (
            <div className="animate-fade-in" data-testid="floor-step-6">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center font-montserrat">
                Pattern Layout
              </h1>
              <p className="text-gray-600 text-center mb-8">
                How would you like the tiles laid?
              </p>
              <div className="space-y-3">
                {patternOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setFormData({ ...formData, pattern_layout: option.value })}
                    className={`funnel-card w-full ${formData.pattern_layout === option.value ? "selected" : ""}`}
                    data-testid={`pattern-${option.value}`}
                  >
                    <div className="flex-1 text-left">
                      <span className="font-medium text-gray-900 block">{option.label}</span>
                      <span className="text-sm text-gray-500">{option.description}</span>
                    </div>
                    <span className="text-sm font-medium text-primary">{option.multiplier}</span>
                    {formData.pattern_layout === option.value && (
                      <Check className="w-5 h-5 text-primary ml-2" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 7: Additional Options */}
          {currentStep === 7 && (
            <div className="animate-fade-in" data-testid="floor-step-7">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center font-montserrat">
                Additional Options
              </h1>
              <p className="text-gray-600 text-center mb-8">
                Select any additional work required (optional)
              </p>
              <div className="space-y-3">
                {additionalOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => toggleOption(option.value)}
                    className={`funnel-card w-full ${formData.additional_options.includes(option.value) ? "selected" : ""}`}
                    data-testid={`additional-${option.value}`}
                  >
                    <div className="flex-1 text-left">
                      <span className="font-medium text-gray-900 block">{option.label}</span>
                      <span className="text-sm text-gray-500">{option.price}</span>
                    </div>
                    {formData.additional_options.includes(option.value) && (
                      <Check className="w-5 h-5 text-primary" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 8: Photo Upload */}
          {currentStep === 8 && (
            <div className="animate-fade-in" data-testid="floor-step-8">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center font-montserrat">
                Upload Photos
              </h1>
              <p className="text-gray-600 text-center mb-8">
                Photos of your floor area help us provide accurate quotes (optional)
              </p>
              
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-primary transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handlePhotoUpload}
                  className="hidden"
                  id="photo-upload"
                />
                <label htmlFor="photo-upload" className="cursor-pointer">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">Click to upload photos</p>
                  <p className="text-sm text-gray-500">Maximum 5 photos, 5MB each</p>
                </label>
              </div>

              {formData.photos.length > 0 && (
                <div className="grid grid-cols-3 gap-4 mt-6">
                  {formData.photos.map((photo, index) => (
                    <div key={index} className="relative aspect-square rounded-lg overflow-hidden">
                      <img src={photo} alt={`Upload ${index + 1}`} className="w-full h-full object-cover" />
                      <button
                        onClick={() => removePhoto(index)}
                        className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 9: Postcode */}
          {currentStep === 9 && (
            <div className="animate-fade-in" data-testid="floor-step-9">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center font-montserrat">
                Your Location
              </h1>
              <p className="text-gray-600 text-center mb-8">
                Enter your postcode
              </p>
              
              <div className="max-w-sm mx-auto">
                <div className="relative">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    value={formData.postcode}
                    onChange={(e) => {
                      setFormData({ ...formData, postcode: e.target.value.toUpperCase() });
                      setPostcodeValid(null);
                    }}
                    placeholder="e.g. M1 1AA"
                    className="pl-12 h-14 text-lg"
                  />
                </div>
                {postcodeValid !== null && (
                  <p className={`mt-3 text-sm ${postcodeValid ? "text-green-600" : "text-orange-600"}`}>
                    {postcodeValid 
                      ? "✓ Great! We service your area." 
                      : "We are based in Greater Manchester but may travel further depending on the project."}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 10: Estimate */}
          {currentStep === 10 && (
            <div className="animate-fade-in" data-testid="floor-step-10">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center font-montserrat">
                Your Floor Tiling Estimate
              </h1>
              
              {estimate && (
                <div className="bg-gradient-to-br from-primary to-blue-700 rounded-2xl p-8 text-center text-white mb-8">
                  <p className="text-white/80 mb-2">Estimated Floor Tiling Cost</p>
                  <p className="text-4xl md:text-5xl font-bold mb-4" data-testid="floor-estimate">
                    £{estimate.min_estimate.toLocaleString()} – £{estimate.max_estimate.toLocaleString()}
                  </p>
                  <p className="text-white/70 text-sm max-w-md mx-auto">
                    Final quote will be confirmed after inspection.
                  </p>
                </div>
              )}

              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <h3 className="font-semibold text-gray-900 mb-4">Project Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Room:</span>
                    <span className="font-medium capitalize">{formData.room_type?.replace("_", " ")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Floor Area:</span>
                    <span className="font-medium">{formData.floor_sqm} m²</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tile Type:</span>
                    <span className="font-medium capitalize">{formData.tile_type?.replace("_", " ")}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pattern:</span>
                    <span className="font-medium capitalize">{formData.pattern_layout}</span>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <Button 
                  onClick={handleNext}
                  className="bg-primary hover:bg-primary/90 py-6"
                >
                  Book Floor Tiling Quote
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Link to="/finance">
                  <Button variant="outline" className="w-full border-2 border-primary text-primary hover:bg-primary/5 py-6">
                    <CreditCard className="w-5 h-5 mr-2" />
                    Check Finance Options
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* Step 11: Booking & Contact */}
          {currentStep === 11 && (
            <div className="animate-fade-in" data-testid="floor-step-11">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center font-montserrat">
                Book Your Quote
              </h1>
              <p className="text-gray-600 text-center mb-8">
                Select a date and enter your details
              </p>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <Label className="mb-2 block">Select a date</Label>
                  <div className="border rounded-xl p-4 bg-white">
                    <CalendarComponent
                      mode="single"
                      selected={formData.booking_date}
                      onSelect={(date) => setFormData({ ...formData, booking_date: date, booking_time: "" })}
                      disabled={(date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        return date < today || date.getDay() === 0;
                      }}
                      className="rounded-md"
                    />
                  </div>
                </div>

                <div>
                  <Label className="mb-2 block">Select a time</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {getAvailableTimes().map((slot) => (
                      <button
                        key={`${slot.date}-${slot.time}`}
                        onClick={() => slot.available && setFormData({ ...formData, booking_time: slot.time })}
                        className={`time-slot ${formData.booking_time === slot.time ? "selected" : ""} ${!slot.available ? "unavailable" : ""}`}
                        disabled={!slot.available}
                      >
                        {slot.time}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Your full name"
                      className="h-12 mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="Your phone number"
                      className="h-12 mt-1"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    className="h-12 mt-1"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 12: Confirmation */}
          {currentStep === 12 && (
            <div className="animate-fade-in text-center" data-testid="floor-step-12">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-montserrat">
                Quote Booked!
              </h1>
              <p className="text-lg text-gray-600 mb-8">
                Confirmation sent to <strong>{formData.email}</strong>
              </p>

              <div className="bg-gray-50 rounded-xl p-6 max-w-md mx-auto mb-8">
                <h3 className="font-semibold text-gray-900 mb-4">Appointment Details</h3>
                <div className="space-y-2 text-left">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Date:</span>
                    <span className="font-medium">{formData.booking_date ? format(formData.booking_date, "EEEE, MMMM d, yyyy") : "N/A"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Time:</span>
                    <span className="font-medium">{formData.booking_time || "N/A"}</span>
                  </div>
                  {estimate && (
                    <div className="flex justify-between pt-2 border-t border-gray-200 mt-2">
                      <span className="text-gray-500">Estimate:</span>
                      <span className="font-medium text-primary">£{estimate.min_estimate.toLocaleString()} - £{estimate.max_estimate.toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-primary/5 rounded-xl p-6 max-w-md mx-auto mb-8">
                <p className="text-gray-700 mb-2">Questions? Call us:</p>
                <a href="tel:+447547393455" className="text-2xl font-bold text-primary hover:text-primary/80">
                  07547 393 455
                </a>
              </div>

              <Button onClick={() => navigate("/")} size="lg">
                Back to Home
              </Button>
            </div>
          )}

          {/* Navigation Buttons */}
          {currentStep < 10 && (
            <div className="flex items-center justify-between mt-12">
              <Button
                variant="ghost"
                onClick={handleBack}
                disabled={currentStep === 1}
                className="text-gray-600"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleNext}
                className="bg-primary hover:bg-primary/90 px-8"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {currentStep === 11 && (
            <div className="flex items-center justify-between mt-8">
              <Button
                variant="ghost"
                onClick={handleBack}
                className="text-gray-600"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={handleNext}
                disabled={loading || !formData.name || !formData.phone || !formData.email}
                className="bg-primary hover:bg-primary/90 px-8"
              >
                {loading ? "Submitting..." : "Confirm Booking"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FloorTilingPage;
